import type { FC } from "react";
import React, { useEffect } from "react";
import {
  Trans,
  useBrand,
  useTracking,
  useTranslation,
} from "@edreamsodigeo/frontend-core";

import { PRIME_LANDING_PAGE_URL } from "../../common/constants";
import type {
  AlignProps,
  SpaceProps,
  WindowLocation,
} from "../../common/contract";
import PrimeHooverBannerTemplate from "../../common/PrimeHooverBannerTemplate";
import type { PrimeHooverBannerProps } from "./config/contracts";
import {
  whatPrimeBannerOnClick,
  whatPrimeBannerOnLoad,
} from "./config/tracking";
import { brandImages } from "./constants";
import useGetSubscriptionOffers from "~/clientState/hooks/useGetSubscriptionOffers";
import {
  SubscriptionOfferPeriod,
  SubscriptionOfferType,
} from "~/generated/graphql";
import { useFilterOfferByType } from "~/clientState/hooks/useFilterOfferByType";
import { RenewalPrice, renewalTimeKey } from "~/tools/subscriptionUtils";

const openPrimeLandingPage = (location: WindowLocation): void => {
  const { protocol, host } = location;
  window.open(`${protocol}//${host}/${PRIME_LANDING_PAGE_URL}`, "_blank");
};

const PrimeHooverBanner: FC<PrimeHooverBannerProps> = ({
  pageCategoryName,
  isPrimeLoggedUser,
  isOpen,
}) => {
  const { t } = useTranslation();
  const { protocol, host } = window.location;
  const { trackEvent } = useTracking();
  const brand = useBrand();

  const { offers } = useGetSubscriptionOffers({
    offerPeriod: SubscriptionOfferPeriod.FreeTrial,
  });

  const plusOffer = useFilterOfferByType({
    offersList: offers,
    subscriptionOfferType: SubscriptionOfferType.Plus,
  });

  useEffect(() => {
    if (pageCategoryName && isOpen) {
      trackEvent(whatPrimeBannerOnLoad(!!isPrimeLoggedUser, pageCategoryName));
    }
  }, [pageCategoryName, isOpen, trackEvent, isPrimeLoggedUser]);

  const title = {
    text: t("visibility.banner.what.is.prime.title"),
    align: "left" as AlignProps,
    format: "medium",
    minWidth: "270px",
  };
  const image = {
    src: brandImages[brand],
    alt: "PrimeHooverBanner",
    marginRight: "minus_1x_big" as SpaceProps,
    marginBottom: "minus_huge" as SpaceProps,
    height: "238px",
  };
  const button = {
    text: t("visibility.banner.what.is.prime.button"),
    align: "left" as AlignProps,
    onClick: (): void => {
      trackEvent(whatPrimeBannerOnClick(!!isPrimeLoggedUser, pageCategoryName));
      openPrimeLandingPage({ protocol, host });
    },
  };
  const description = {
    text: t("visibility.banner.what.is.prime.description"),
    minWidth: "270px",
  };

  const offer = {
    component: (
      <Trans
        i18nKey={"visibility.banner.what.is.prime.offer"}
        values={{
          renewalTime: t(renewalTimeKey(plusOffer?.renewalDuration)),
        }}
        components={{
          renewalPrice: (
            <RenewalPrice
              currency={plusOffer?.renewalPrice.currency}
              amount={plusOffer?.renewalPrice.amount}
            />
          ),
        }}
      />
    ),
    minWidth: "270px",
  };

  return (
    <PrimeHooverBannerTemplate
      logo={true}
      title={title}
      description={description}
      offer={offer}
      image={image}
      button={button}
    />
  );
};

export default PrimeHooverBanner;
